<template>
    <div>
        <div v-if="loading" class="d-flex justify-content-center align-items-center min-vh-100">
            <fco-spinner />
        </div>

        <div class="d-flex justify-content-center align-items-center min-vh-100">
            <div v-if="hasAuthError" class="errorMessage d-flex flex-column justify-content-center align-items-center text-center">
                <p class="mb-3">{{ fcoM('rs.authentication.failed', 'Authentication failed. Please try again.') }}</p>
                <button @click="punchout">
                    {{ fcoM('common.tryAgain', 'Try again') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { clearAndSelectPartType } from '@/common/services/partTypeService';
import { mapGetters, mapState } from 'vuex';
import vsMixin from '../../../common/mixins/vsMixin';
import { trackUserLogIn } from '../../../fcoModules/analytics';
import { VehicleSelectionMethod } from '../../../common/constants/analytics';
import analyticsMixin from '../../../common/mixins/analyticsMixin';
import featureFlagsMixin from '../../../common/mixins/featureFlagsMixin';
import vehicleAddedAnalyticsMixin from '../../../common/mixins/vehicleAddedAnalyticsMixin';
import { trackPunchoutSession } from '../../../common/store/store-utils';
import { formatResolvedPartTypes } from '../../../common/utilities/productUtils';

export default {
    name: 'Punchout',
    mixins: [vsMixin, featureFlagsMixin, analyticsMixin, vehicleAddedAnalyticsMixin],
    data() {
        return {
            loading: true,
            hasAuthError: false,
        };
    },
    computed: {
        ...mapState('vehicleSelector', ['currentVehicle']),
        ...mapGetters(['isAuthExpired', 'isAuthorizedUser', 'userDefaultLandingPage']),
        queryParams() {
            const { otlLoginKey, vin, vehicleId, engineId, submodelId, year, make, model, callbackUrl, transactionId, buyerCookie, partTypeIds } =
                !this.isSPA ? Object.fromEntries(new URLSearchParams(window.location.search)) : this.$route.query;

            return {
                otlLoginKey,
                vin,
                vehicleId,
                engineId,
                submodelId,
                year,
                make,
                model,
                callbackUrl,
                transactionId,
                buyerCookie,
                partTypeIds,
            };
        },
    },
    methods: {
        async punchout() {
            this.loading = true;
            this.hasAuthError = '';

            if ((!this.isSPA && !this.isAuthorizedUser) || this.isAuthExpired) {
                await this.authenticate();
            }

            const { user } = this.$store.state;

            trackUserLogIn({
                loginSuccessful: !this.hasAuthErrore,
                userEmail: user?.loginName || null,
                userId: user?.userId || null,
                isSSO: true,
            });

            if (this.hasAuthError) {
                this.loading = false;
                return;
            }

            await this.loadFeatureFlags();

            this.saveCallBackUrl();
            this.saveTransactionId();
            this.saveBuyerCookie();

            await this.selectVehicle();
            await this.selectPartTypes();

            // remove the query parameters. we don't need them anymore
            if (!this.isSPA) {
                window.history.replaceState(null, '', window.location.pathname);
            } else {
                this.$router.replace({ path: this.$route.path, query: {} });
            }

            await this.$store.dispatch('refetchUserCriticalData');

            this.$store.commit('setIsPunchoutComplete', true);
            this.loading = false;
        },
        async authenticate() {
            try {
                if (!this.isSPA) {
                    await this.$store.dispatch('requestIfIdle', ['getUser']);
                    if (!this.isAuthorizedUser) {
                        throw new Error('User not autheneticated: invalid legacy session');
                    }
                    return;
                }

                const { otlLoginKey } = this.queryParams;

                if (!otlLoginKey) {
                    throw new Error('Missing one time login key');
                }

                const response = await axios.post(this.fcoUrl(`/auth/rest/oneTimeLogin?key=${otlLoginKey}`));

                trackPunchoutSession();

                this.$store.commit('setAuthTokens', response.data);
                this.$store.dispatch('startRefreshTimer');
            } catch (error) {
                this.hasAuthError = true;
            }
        },

        async selectVehicle() {
            const { vin, vehicleId, engineId, submodelId, year, make, model } = this.queryParams;

            if (!vin && !vehicleId && !(year && make && model)) return;

            try {
                let vehicleSelectionMethod = VehicleSelectionMethod.BY_TYPE;
                let vehicle;

                if (!this.isEntSearchEnabled) {
                    await this.$store.dispatch('requestIfIdle', ['vehicleSelector/getCurrentVehicle', 'miniQuote/getQuoteData']);
                    if (!this.currentVehicle?.id) {
                        throw new Error('Vehicle selection failed');
                    }

                    this.$_analyticsMixin_trackVehicleAdded(vehicleSelectionMethod);
                    return;
                }

                if (vin) {
                    vehicle = await this.selectVehicleFromVIN({ vin });
                    vehicleSelectionMethod = VehicleSelectionMethod.BY_VIN;
                } else if (vehicleId) {
                    vehicle = await this.selectVehicleFromVehicleData({ baseVehicleId: vehicleId, engineId, submodelId });
                } else {
                    const { data: vehicleMakes } = await axios.get(this.fcoUrl(`/vehicle/select/v2/models?year=${year}&makeId=${make}`));
                    const baseVehicleId = vehicleMakes?.find(({ model: { id: modelId } }) => modelId === model)?.baseVehicleId;
                    vehicle = await this.selectVehicleFromVehicleData({ baseVehicleId, engineId });
                }

                if (!vehicle) {
                    throw new Error('Vehicle selection failed');
                }
                if (vehicle?.id) {
                    this.$store.commit('vehicleSelector/setCurrentVehicle', vehicle);
                    this.$_analyticsMixin_trackVehicleAdded(vehicleSelectionMethod);
                }
            } catch (error) {
                this.$fcoToast.error(this.fcoM('rs.homeMain.errorLoadingVehicle', 'There was an error loading the vehicle, please try again later'));
            }
        },

        async selectPartTypes() {
            const partTypeIds = this.queryParams.partTypeIds ? this.queryParams.partTypeIds.split(',') : null;

            if (!partTypeIds || partTypeIds.length === 0) return;

            let hasShownPartSelectionError = false;

            try {
                const { data } = await axios.post(this.fcoUrl('/parttype/node/v2/resolved'), {
                    vehicle: this.currentVehicle,
                    selectedPartTypes: partTypeIds.map((id) => ({
                        partTypeId: id,
                        parentPartTypeId: id,
                        platformId: '05',
                    })),
                });

                this.$store.dispatch('partSelection/savePartLookup', {
                    partTypes: data.results,
                    resolvedPartTypes: formatResolvedPartTypes(data.results),
                    fullResolvedDetails: data.results,
                    vehicle: this.vehicle,
                    origin: origin || null,
                });

                if (data.errorMessages) {
                    this.$fcoToast.error(this.fcoM('rs.partSelection.errorLoad', 'An error occurred while loading your parts. Please try again.'));
                    hasShownPartSelectionError = true;
                }

                await clearAndSelectPartType(data.results);
            } catch (error) {
                if (!hasShownPartSelectionError) {
                    this.$fcoToast.error(this.fcoM('rs.partSelection.errorLoad', 'An error occurred while loading your parts. Please try again.'));
                }
            }
        },

        async saveCallBackUrl() {
            const { callbackUrl } = this.queryParams;
            if (!callbackUrl) {
                return;
            }
            this.$store.commit('setCxmlCallbackUrl', callbackUrl);
        },

        async saveTransactionId() {
            const { transactionId } = this.queryParams;
            if (!transactionId) {
                return;
            }
            this.$store.commit('setTransactionId', transactionId);
        },

        async saveBuyerCookie() {
            const { buyerCookie } = this.queryParams;
            if (!buyerCookie) {
                return;
            }
            this.$store.commit('setBuyerCookie', buyerCookie);
        },
    },

    async mounted() {
        this.$store.commit('vehicleSelector/setVsProp', { prop: 'preventNavigationOnVehicleUpdate', value: true });
        await this.$store.dispatch('requestIfIdle', ['getAppData']);
        this.punchout();
    },
};
</script>

<style scoped>
.errorMessage {
    text-align: center;
}
</style>
